<script>
  /**
   * Markdown.svelte
   * Renders a markdown string as sanitised HTML.
   *
   * Usage:
   *   <Markdown content={someString} />
   *   <Markdown content={someString} prose />   ← wider line-width for long-form text
   *
   * Dependencies (install once):
   *   npm install marked dompurify
   */
  import { marked } from 'marked';
  import DOMPurify from 'dompurify';

  export let content = '';
  export let prose   = false;   // wider container for long-form reading

  // Configure marked — no GFM tables for now, keep it simple
  marked.setOptions({
    gfm:    true,
    breaks: true,   // single newline → <br>, like most users expect
  });

  $: html = content?.trim()
    ? DOMPurify.sanitize(marked.parse(content))
    : '';
</script>

{#if html}
  <div class="md" class:prose>{@html html}</div>
{/if}

<style>
  .md {
    font-family: 'Rajdhani', sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
    color: #a0a0a0;
    line-height: 1.7;
    word-break: break-word;
  }

  .md.prose {
    max-width: 560px;
  }

  /* ── Block elements ── */
  .md :global(p)  { margin: 0 0 0.75em; }
  .md :global(p:last-child) { margin-bottom: 0; }

  .md :global(h1),
  .md :global(h2),
  .md :global(h3),
  .md :global(h4) {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 700;
    color: #c8c8c8;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin: 1em 0 0.4em;
    line-height: 1.2;
  }
  .md :global(h1) { font-size: 1.15rem; color: #e0e0e0; border-bottom: 1px solid #1e3d00; padding-bottom: 0.3em; }
  .md :global(h2) { font-size: 1rem;    color: #c8c8c8; }
  .md :global(h3) { font-size: 0.85rem; color: #aaa; }
  .md :global(h4) { font-size: 0.75rem; color: #888; }

  .md :global(ul),
  .md :global(ol) {
    margin: 0 0 0.75em 1.25em;
    padding: 0;
  }
  .md :global(li) { margin-bottom: 0.2em; }

  .md :global(blockquote) {
    margin: 0.75em 0;
    padding: 0.5em 0.85em;
    border-left: 3px solid #3a6b00;
    background: #0d1a00;
    border-radius: 0 2px 2px 0;
    color: #777;
    font-style: italic;
  }
  .md :global(blockquote p) { margin: 0; }

  .md :global(hr) {
    border: none;
    border-top: 1px solid #1e3d00;
    margin: 1em 0;
  }

  /* ── Inline elements ── */
  .md :global(strong) { font-weight: 700; color: #c8c8c8; }
  .md :global(em)     { font-style: italic; color: #999; }

  .md :global(a) {
    color: #4a7c00;
    text-decoration: underline;
    text-decoration-color: #1e3d00;
    transition: color 0.15s, text-decoration-color 0.15s;
  }
  .md :global(a:hover) { color: #6db800; text-decoration-color: #6db800; }

  /* ── Code ── */
  .md :global(code) {
    font-family: 'Courier New', Courier, monospace;
    font-size: 0.82em;
    background: #0d1a00;
    border: 1px solid #1e3d00;
    border-radius: 2px;
    padding: 0.1em 0.4em;
    color: #6db800;
  }

  .md :global(pre) {
    background: #0a0a0a;
    border: 1px solid #1e3d00;
    border-radius: 3px;
    padding: 0.85em 1em;
    overflow-x: auto;
    margin: 0.75em 0;
  }
  .md :global(pre code) {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.85em;
    color: #6db800;
  }

  /* ── Images ── */
  .md :global(img) {
    max-width: 100%;
    border-radius: 2px;
    border: 1px solid #1e3d00;
    display: block;
    margin: 0.5em auto;
  }
</style>
